import { useEffect, useMemo, useRef, useState } from "react";
import { Package, Search } from "lucide-react";
import {
  AddonRow,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Gallery,
} from "@fm/ui";
import {
  ADDON_CATEGORIES,
  addonCategoryLabel,
  addonGalleryImages,
  addonPhotoUrl,
} from "@fm/api/addons";
import type { Addon } from "@fm/api/addons";

export interface AddonSelectorProps {
  addons: Addon[];
  selectedAddonIds: string[];
  onSelectionChange: (addonIds: string[]) => void;
  isLoading?: boolean;
  className?: string;
  /** Nights of the stay. Enables the trip total beside per-day prices. */
  nights?: number;
  /** When given, a sticky footer with the running total and Done renders. */
  onDone?: () => void;
}

const UNIT_LABELS: Record<string, string> = {
  per_day: "per day",
  per_rental: "per rental",
  per_hour: "per hour",
  per_week: "per week",
  one_time: "one time",
};

const OTHER = "other";

function parsePrice(price: string | number): number | null {
  const n = typeof price === "string" ? parseFloat(price) : price;
  return Number.isFinite(n) ? n : null;
}

const money = (n: number) => `$${n.toFixed(2)}`;

/**
 * What an add-on adds to the trip: per_day multiplies by nights, one_time and
 * per_rental are charged once. Usage-based (per_hour, per_mile), per_week and
 * unknown units have no knowable trip total, so they return null instead of a
 * guess.
 */
export function addonTripTotal(
  addon: Addon,
  nights: number | undefined,
): number | null {
  const price = parsePrice(addon.price);
  if (price === null) return null;
  switch (addon.unit as string) {
    case "one_time":
    case "per_rental":
      return price;
    case "per_day":
      return nights !== undefined && nights > 0 ? price * nights : null;
    default:
      return null;
  }
}

/** Sum of the selected add-ons' trip totals; flags selections it cannot price. */
export function addonsRunningTotal(
  addons: Addon[],
  selectedAddonIds: string[],
  nights: number | undefined,
): { total: number; hasUnpriced: boolean } {
  let total = 0;
  let hasUnpriced = false;
  for (const addon of addons) {
    if (!selectedAddonIds.includes(addon.id)) continue;
    const line = addonTripTotal(addon, nights);
    if (line === null) hasUnpriced = true;
    else total += line;
  }
  return { total, hasUnpriced };
}

const categoryOf = (addon: Addon): string => addon.category ?? OTHER;

/**
 * Add-ons window body: search, type tabs with counts, compact rows with an
 * Add / Added button and, when `onDone` is given, a sticky footer with the
 * running total.
 *
 * Required add-ons are auto-selected and cannot be toggled. A read-only
 * "View details" dialog (name, description, category, photo gallery) never
 * mutates selection.
 */
export function AddonSelector({
  addons,
  selectedAddonIds,
  onSelectionChange,
  isLoading = false,
  className = "",
  nights,
  onDone,
}: AddonSelectorProps) {
  // Auto-select required addons on mount or when addons change
  useEffect(() => {
    const requiredAddonIds = addons
      .filter((addon) => addon.required)
      .map((addon) => addon.id);

    // Only update if there are required addons not yet selected
    const missingRequired = requiredAddonIds.filter(
      (id) => !selectedAddonIds.includes(id),
    );

    if (missingRequired.length > 0) {
      const newSelection = [
        ...new Set([...selectedAddonIds, ...requiredAddonIds]),
      ];
      onSelectionChange(newSelection);
    }
  }, [addons, selectedAddonIds, onSelectionChange]);

  const handleToggleAddon = (addonId: string, isRequired: boolean) => {
    if (isRequired) return; // Cannot deselect required addons

    const isCurrentlySelected = selectedAddonIds.includes(addonId);

    if (isCurrentlySelected) {
      onSelectionChange(selectedAddonIds.filter((id) => id !== addonId));
    } else {
      onSelectionChange([...selectedAddonIds, addonId]);
    }
  };

  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return addons;
    return addons.filter((addon) =>
      `${addon.name} ${addon.description ?? ""}`.toLowerCase().includes(q),
    );
  }, [addons, query]);

  const tabs = useMemo(() => {
    const present = new Set(addons.map(categoryOf));
    return [
      { value: "all", label: "All", count: searched.length },
      ...ADDON_CATEGORIES.filter((c) => present.has(c.value)).map((c) => ({
        value: c.value as string,
        label: c.label,
        count: searched.filter((a) => categoryOf(a) === c.value).length,
      })),
    ];
  }, [addons, searched]);

  const visible =
    activeTab === "all"
      ? searched
      : searched.filter((addon) => categoryOf(addon) === activeTab);

  const { total, hasUnpriced } = addonsRunningTotal(
    addons,
    selectedAddonIds,
    nights,
  );
  const selectedCount = addons.filter((a) =>
    selectedAddonIds.includes(a.id),
  ).length;

  const rowLabels = (addon: Addon) => {
    const price = parsePrice(addon.price);
    const priceLabel = price === null ? String(addon.price) : money(price);
    const unitLabel = UNIT_LABELS[addon.unit] || addon.unit;
    const trip =
      addon.unit === "per_day" ? addonTripTotal(addon, nights) : null;
    const tripLabel =
      trip === null || nights === undefined
        ? null
        : `${money(trip)} for ${nights} ${nights === 1 ? "night" : "nights"}`;
    return { priceLabel, unitLabel, tripLabel };
  };

  // Read-only detail dialog. One <Dialog> hoisted out of the .map() and
  // driven by `detailAddonId`, so nesting a per-card dialog inside the
  // repeated card markup never happens (D2). The detail control's own
  // button ref is tracked per add-on so focus can return to the exact
  // control that opened the dialog once it closes (spec requirement:
  // focus returns to the button that opened the dialog).
  const [detailAddonId, setDetailAddonId] = useState<string | null>(null);
  const detailButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastOpenedAddonIdRef = useRef<string | null>(null);
  const detailAddon =
    addons.find((addon) => addon.id === detailAddonId) ?? null;

  const openDetail = (addonId: string) => {
    lastOpenedAddonIdRef.current = addonId;
    setDetailAddonId(addonId);
  };

  if (isLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (addons.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-500 ${className}`}>
        <p>No add-ons available for this selection</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          aria-label="Search add-ons"
          placeholder="Search add-ons"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div
        role="tablist"
        aria-label="Add-on types"
        className="flex flex-wrap gap-2"
      >
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeTab === tab.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-foreground hover:bg-muted"
            }`}
          >
            {tab.label}
            <span className="text-xs opacity-80">{tab.count}</span>
          </button>
        ))}
      </div>

      <div role="tabpanel" className="space-y-2">
        {visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {query.trim()
              ? `No add-ons match "${query.trim()}".`
              : "No add-ons in this type."}
          </p>
        ) : (
          visible.map((addon) => (
            <AddonRow
              key={addon.id}
              name={addon.name}
              description={addon.description}
              {...rowLabels(addon)}
              photoUrl={
                addon.photos.length > 0 ? addonPhotoUrl(addon, "card") : null
              }
              selected={selectedAddonIds.includes(addon.id)}
              required={addon.required}
              onToggle={() => handleToggleAddon(addon.id, addon.required)}
              onDetails={() => openDetail(addon.id)}
              detailsExpanded={detailAddonId === addon.id}
              detailsButtonRef={(node) => {
                detailButtonRefs.current[addon.id] = node;
              }}
            />
          ))
        )}
      </div>

      {onDone && (
        <div className="sticky bottom-0 -mx-1 flex items-center justify-between gap-3 border-t border-border bg-background px-1 pb-1 pt-3">
          <div>
            <div className="text-sm text-muted-foreground">
              {selectedCount === 0
                ? "No add-ons selected"
                : `${selectedCount} ${selectedCount === 1 ? "add-on" : "add-ons"} selected`}
            </div>
            <div
              data-testid="addons-total"
              className="text-lg font-semibold text-foreground"
            >
              {money(total)}
            </div>
            {hasUnpriced && (
              <div className="text-xs text-muted-foreground">
                Usage-based add-ons are not included.
              </div>
            )}
          </div>
          <Button type="button" onClick={onDone}>
            Done
          </Button>
        </div>
      )}

      <Dialog
        open={detailAddonId !== null}
        onOpenChange={(open) => {
          if (!open) setDetailAddonId(null);
        }}
      >
        {/*
          z-[99999] is intentional, not arbitrary: this package ships no
          Tailwind pass of its own (it imports the prebuilt @fm/ui
          stylesheet), so only classes some other package already generated
          exist in that CSS at all. z-[99999] is confirmed present there and
          replaces the base z-[10000] via tailwind-merge conflict
          resolution — z-[10001] would silently do nothing.
        */}
        <DialogContent
          className="z-[99999]"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const idToFocus = lastOpenedAddonIdRef.current;
            if (idToFocus) {
              detailButtonRefs.current[idToFocus]?.focus();
            }
          }}
        >
          {detailAddon && (
            <>
              <DialogHeader>
                <DialogTitle>{detailAddon.name}</DialogTitle>
                {detailAddon.description && (
                  <DialogDescription>
                    {detailAddon.description}
                  </DialogDescription>
                )}
              </DialogHeader>

              {addonCategoryLabel(detailAddon.category) && (
                <Badge variant="secondary" className="w-fit">
                  {addonCategoryLabel(detailAddon.category)}
                </Badge>
              )}

              <Gallery
                images={addonGalleryImages(detailAddon, "original")}
                emptyState={
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <Package
                      className="h-10 w-10"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                    <span className="text-sm">No photos available</span>
                  </div>
                }
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
